import { ArrowRight, Eye, ExternalLink, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";

const PREVIEW_URL = process.env.REACT_APP_BACKEND_URL || "";

// Real properties that exist in the system
const realProperties = [
  {
    slug: "casa-en-venta-en-la-moraleja",
    title: "Casa en venta en La Moraleja",
    price: "$1,900,000",
    color: "#22c55e"
  },
  {
    slug: "casa-en-venta-en-abie-residencial",
    title: "Casa en venta en Abie Residencial",
    price: "$3,500,000",
    color: "#a033fd"
  }
];

export const LandingExamples = () => {
  return (
    <section id="ejemplos" className="py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-pink-100 text-brand-pink px-4 py-1 rounded-full text-sm font-medium mb-4">
            Ejemplos Reales
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Así lucen las <span className="text-brand-purple">Landing Pages</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Mira ejemplos reales de propiedades publicadas en nuestra plataforma
          </p>
        </div>

        {/* Landing Page Mockup - Real Preview */}
        <div className="mb-12 sm:mb-16">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl p-4 pt-6 sm:p-8 md:p-12 overflow-hidden" style={{maxWidth: '100%'}}>
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-center overflow-hidden">
              {/* Left - Description */}
              <div className="text-white min-w-0 overflow-hidden max-w-full pt-2">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6">
                  Diseño profesional que <span className="text-brand-purple">convierte</span>
                </h3>
                <p className="text-gray-300 text-sm sm:text-base mb-5 sm:mb-8">
                  Cada landing page está diseñada para captar la atención del visitante y convertirlo en prospecto.
                </p>
                
                <div className="space-y-2.5 sm:space-y-3 mb-5 sm:mb-8">
                  {[
                    "Header con imagen destacada",
                    "Precio y características visibles",
                    "Galería de fotos interactiva",
                    "Video de YouTube / Vimeo",
                    "Mapa de ubicación",
                    "Formulario de contacto",
                    "Dashboard de leads + email",
                    "Botón de WhatsApp directo"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-brand-purple flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  {realProperties.map((prop) => (
                    <Link 
                      key={prop.slug}
                      to={`/propiedades/${prop.slug}`}
                      target="_blank"
                      className="w-full sm:w-auto"
                    >
                      <Button 
                        className="bg-white text-gray-900 hover:bg-gray-100 w-full sm:w-auto text-sm"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Ver Ejemplo Real
                        <ExternalLink className="w-3 h-3 ml-2" />
                      </Button>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Right - Real Landing Page Preview */}
              <div className="relative overflow-hidden">
                {/* Desktop mockup with real iframe */}
                <div className="bg-white rounded-xl shadow-2xl overflow-hidden sm:transform sm:rotate-1 sm:hover:rotate-0 transition-transform duration-500">
                  {/* Browser bar */}
                  <div className="bg-gray-100 px-3 sm:px-4 py-2 flex items-center gap-2 border-b border-gray-200">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 bg-white rounded px-2 sm:px-3 py-1 text-xs text-gray-500 ml-2 sm:ml-4 font-mono truncate">
                      inmopages.app/propiedades/...
                    </div>
                  </div>
                  
                  {/* Real page preview via iframe */}
                  <div className="relative h-[250px] sm:h-[420px] overflow-hidden">
                    <iframe
                      src={`${PREVIEW_URL}/propiedades/casa-en-venta-en-abie-residencial`}
                      title="Preview de Landing Page"
                      className="border-0 pointer-events-none w-[1280px] h-[900px] origin-top-left scale-[0.29] sm:scale-[0.39]"
                      loading="lazy"
                      scrolling="no"
                    />
                  </div>
                </div>

                {/* Mobile mockup with real iframe - hidden on small screens */}
                <div className="hidden sm:block absolute -bottom-4 -left-8 w-36 bg-white rounded-2xl shadow-2xl overflow-hidden transform -rotate-6 hover:rotate-0 transition-transform duration-500 border-4 border-gray-800">
                  <div className="h-4 bg-gray-800 flex items-center justify-center">
                    <div className="w-8 h-1.5 bg-gray-600 rounded-full" />
                  </div>
                  <div className="relative h-56 overflow-hidden">
                    <iframe
                      src={`${PREVIEW_URL}/propiedades/casa-en-venta-en-abie-residencial`}
                      title="Preview móvil"
                      className="w-[375px] h-[667px] border-0 pointer-events-none"
                      style={{ 
                        transform: 'scale(0.34)', 
                        transformOrigin: 'top left',
                      }}
                      loading="lazy"
                      scrolling="no"
                    />
                  </div>
                </div>

                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-purple/20 rounded-full blur-2xl" />
                <div className="absolute -bottom-8 right-12 w-32 h-32 bg-brand-pink/20 rounded-full blur-2xl" />
              </div>
            </div>
          </div>
        </div>

        {/* CTA with 5 min badge */}
        <div className="text-center mt-12">
          <div className="inline-block bg-gradient-to-br from-brand-purple to-brand-pink p-1 rounded-2xl mb-6">
            <div className="bg-white rounded-xl p-8">
              <p className="text-5xl font-bold text-brand-purple mb-2">5 min</p>
              <p className="text-gray-600">para crear tu primera landing</p>
            </div>
          </div>
          
          <div>
            <Button
              size="lg"
              className="bg-brand-purple hover:bg-brand-pink text-white px-8 text-lg"
              onClick={(e) => {
                e.preventDefault();
                if (window.firePixelAndNavigate) {
                  window.firePixelAndNavigate('Lead', { content_name: 'CTA Ejemplos Reales' }, '/register');
                } else {
                  window.location.href = '/register';
                }
              }}
            >
              Crear Mi Landing
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
